import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function statusColor(status) {
  if (status === "approved") return "text-emerald-400";
  if (status === "rejected") return "text-red-400";
  return "text-amber-400";
}

export default function Withdraw() {
  const { user, refresh } = useAuth();
  const [amount, setAmount] = useState(15);
  const [tpass, setTpass] = useState("");
  const [rows, setRows] = useState([]);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [busy, setBusy] = useState(false);
  const [bep20Input, setBep20Input] = useState("");
  const [bep20Busy, setBep20Busy] = useState(false);
  const [bep20Msg, setBep20Msg] = useState({ type: "", text: "" });

  const savedBep20 = (user?.bep20_address || "").trim();

  const saveBep20 = async () => {
    setBep20Busy(true); setBep20Msg({ type: "", text: "" });
    try {
      await api.post("/me/bep20-address", { address: bep20Input.trim() });
      setBep20Msg({ type: "ok", text: "BEP20 address saved and locked." });
      await refresh();
    } catch (e) {
      setBep20Msg({ type: "err", text: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally { setBep20Busy(false); }
  };

  const load = useCallback(async () => {
    try {
      const r = await api.get("/me/withdrawals");
      setRows(r.data.rows);
    } catch (err) {
      console.warn("Withdrawals load failed:", err?.message);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const [playActive, setPlayActive] = useState(null);
  useEffect(() => {
    api.get("/me/play-card")
      .then((r) => setPlayActive(!!r.data?.play_eligible))
      .catch(() => setPlayActive(false));
  }, []);

  const subActive = Number(user?.package_value || 0) > 0;
  const eligible = subActive && playActive === true;
  const eligibilityMsg = !subActive
    ? "You need an active subscription to withdraw. Please activate a package first."
    : playActive === false
      ? "You must be actively playing the Play Card game to withdraw — fund your Game Fund and drop at least one card first."
      : "";

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setMsg({ type: "", text: "" });
    try {
      await api.post("/wallet/withdraw", { amount: Number(amount), t_pass: tpass });
      setMsg({ type: "ok", text: "Withdrawal submitted for admin approval." });
      setTpass("");
      await load(); await refresh();
    } catch (e) {
      setMsg({ type: "err", text: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally { setBusy(false); }
  };

  const todayIsWd = [1, 5].includes(new Date().getDay()); // Mon=1, Fri=5

  // Temporarily disable user-initiated withdrawal requests. Set to true to re-enable.
  const WITHDRAWALS_ENABLED = false;

  return (
    <div className="space-y-8">
      <div>
        <div className="overline">Withdraw</div>
        <h1 className="font-display text-4xl mt-1">Cash Out</h1>
        <p className="text-neutral-400 mt-2 max-w-2xl">
          Withdrawals open Mon / Fri only. Minimum $15 (multiples of $15). 20% deduction: 10% Admin + 10% Donation Club.
        </p>
      </div>

      <form onSubmit={submit} className="lt-card p-6 max-w-xl space-y-4" data-testid="withdraw-form">
        <div data-testid="bep20-section">
          <label className="overline block mb-2">BEP20 Address (USDT · BSC payout)</label>
          {savedBep20 ? (
            <div className="px-4 py-3 rounded-lg border border-[#D4AF37]/30 bg-[#D4AF37]/10 text-sm tabular break-all gold-text" data-testid="bep20-locked">
              {savedBep20}
              <span className="block text-[10px] text-neutral-400 mt-1 tracking-widest uppercase">Locked · contact admin to change</span>
            </div>
          ) : (
            <>
              <input
                className="lt-input tabular"
                value={bep20Input}
                onChange={(e) => setBep20Input(e.target.value)}
                placeholder="0x… (paste your BEP20 wallet address)"
                data-testid="bep20-input"
              />
              <p className="text-[11px] text-amber-300/80 mt-1">Set this once. After saving it is fixed and required for withdrawals.</p>
              {bep20Msg.text && <div className={`text-sm mt-1 ${bep20Msg.type === "ok" ? "gold-text" : "text-red-400"}`} data-testid="bep20-msg">{bep20Msg.text}</div>}
              <button type="button" onClick={saveBep20} disabled={bep20Busy || !bep20Input.trim()} className="lt-btn-secondary mt-2 disabled:opacity-50" data-testid="bep20-save-btn">
                {bep20Busy ? "Saving…" : "Save BEP20 Address"}
              </button>
            </>
          )}
        </div>
        <div>
          <label className="overline block mb-2">Amount (USD, multiples of $15)</label>
          <input type="number" min="15" step="15" className="lt-input tabular" value={amount} onChange={(e) => setAmount(e.target.value)} required data-testid="withdraw-amount-input" />
        </div>
        <div className="grid grid-cols-3 gap-3 text-sm">
          <div><div className="overline">Gross</div><div className="tabular mt-1">${Number(amount || 0).toFixed(2)}</div></div>
          <div><div className="overline">Deduction (20%)</div><div className="tabular mt-1">${(Number(amount || 0) * 0.2).toFixed(2)}</div></div>
          <div><div className="overline">You receive</div><div className="tabular mt-1 gold-text">${(Number(amount || 0) * 0.8).toFixed(2)}</div></div>
        </div>
        <div>
          <label className="overline block mb-2">Transaction password</label>
          <input type="password" className="lt-input" value={tpass} onChange={(e) => setTpass(e.target.value)} required data-testid="withdraw-tpass-input" />
        </div>
        {!todayIsWd && <div className="text-sm text-amber-400">Today is not a withdrawal day. Allowed only on Mon, Fri.</div>}
        {eligibilityMsg && <div className="text-sm text-amber-400" data-testid="withdraw-eligibility-msg">{eligibilityMsg}</div>}
        {msg.text && <div className={`text-sm ${msg.type === "ok" ? "gold-text" : "text-red-400"}`} data-testid="withdraw-msg">{msg.text}</div>}
        {!WITHDRAWALS_ENABLED && (
          <div className="text-sm text-amber-400 rounded-lg border border-amber-500/30 bg-amber-500/[0.06] px-4 py-3" data-testid="withdraw-disabled-msg">
            Withdrawals are temporarily unavailable. Please check back later.
          </div>
        )}
        <button className="lt-btn-primary disabled:opacity-50" disabled={!WITHDRAWALS_ENABLED || busy || !eligible} data-testid="withdraw-submit-btn">
          {busy ? "Submitting…" : "Request withdrawal"}
        </button>
      </form>

      <div className="lt-card p-6">
        <div className="overline mb-3">Recent requests</div>
        <div className="overflow-x-auto -mx-1">
        <table className="lt-table">
          <thead><tr><th>When</th><th>Amount</th><th>Deduction</th><th>Net</th><th>Status</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} data-testid={`withdrawal-row-${r.id}`}>
                <td className="text-neutral-400">{new Date(r.created_at).toLocaleString()}</td>
                <td className="tabular">${r.amount.toFixed(2)}</td>
                <td className="tabular">${r.deduction.toFixed(2)}</td>
                <td className="tabular gold-text">${r.net.toFixed(2)}</td>
                <td><span className={`uppercase tracking-widest text-xs ${statusColor(r.status)}`}>{r.status}</span></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="text-center text-neutral-500 py-6">No withdrawal requests yet.</td></tr>}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
